import { useState, useRef } from 'react';
import { useDiagramData } from '@/hooks/useDiagramData';
import { useApp } from '@/contexts/AppContext';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { useWriteBackLock } from '@/hooks/useWriteBackLock';
import { useProject } from '@/contexts/ProjectContext';
import { READ_ONLY_WRITE_BACK_MESSAGE, STANDALONE_WRITE_BACK_MESSAGE } from '@/lib/projectVersionState';
import { useReviewCommentsIndex } from '@/hooks/useReviewCommentsIndex';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Copy, Search, Pencil, Loader2, CheckCircle2, AlertTriangle, Filter } from 'lucide-react';
import ReviewCommentsModal, { ReviewCommentsIndicator } from '@/components/review/ReviewCommentsModal';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { threatcatService } from '@/services/threatcatService';
import { draftDiagramService, type DraftDiagramOut } from '@/services/draftDiagramService';
import { ThreatCatAuthError } from '@/lib/errors';
import ConnectModal from '@/components/threatcat/ConnectModal';
import type { Path } from '@/types/diagram';

interface EditDraft {
  name: string;
  description: string;
  protocol: string;
  authentication: string;
}

interface ConnectionsTabProps {
  draftId?: number;
  onDraftUpdated?: (draft: DraftDiagramOut) => void;
}

export default function ConnectionsTab({ draftId, onDraftUpdated }: ConnectionsTabProps = {}) {
  const isDraft = draftId != null;
  const { currentDiagram, entitiesMap, getEntityBoundaryChain } = useDiagramData();
  const { diagrams, currentDiagramIndex, setAppState } = useApp();
  const { cookie, getCookie, disconnect } = useThreatCat();
  const { isLocked: isWriteBackLocked, message: writeBackLockMessage } = useWriteBackLock();
  const reviewIndex = useReviewCommentsIndex();
  const { projectId, detail } = useProject();
  const isStandaloneDiagram = !isDraft && projectId == null;
  const isReadOnly = detail?.permission === 'read_only';
  const isPathWriteLocked = !isDraft && isWriteBackLocked;
  const isEditDisabled = isStandaloneDiagram || isReadOnly || isPathWriteLocked;
  const editDisabledReason = isStandaloneDiagram
    ? STANDALONE_WRITE_BACK_MESSAGE
    : isReadOnly
      ? READ_ONLY_WRITE_BACK_MESSAGE
      : isPathWriteLocked
        ? writeBackLockMessage
        : null;

  const [searchTerm, setSearchTerm] = useState('');
  const [quickFilter, setQuickFilter] = useState<string>('all');
  const [copied, setCopied] = useState(false);
  const [reviewModalPathId, setReviewModalPathId] = useState<number | null>(null);

  // ── Edit path state ──────────────────────────────────────────────────────────
  const [editingPath, setEditingPath] = useState<Path | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft>({ name: '', description: '', protocol: '', authentication: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const pendingSavePathRef = useRef<Path | null>(null);

  if (!currentDiagram) return null;

  const missingLabelCount = currentDiagram.paths.filter(p => !p.name?.trim()).length;
  const missingDescCount = currentDiagram.paths.filter(p => !p.description?.trim()).length;
  const missingAuthCount = currentDiagram.paths.filter(p => !p.authentication?.trim()).length;
  const activeReviewPath =
    reviewModalPathId !== null
      ? currentDiagram.paths.find((p) => p.id === reviewModalPathId) ?? null
      : null;
  const activeReviewPathComments = activeReviewPath
    ? reviewIndex.getPathComments(activeReviewPath.id, currentDiagram.id)
    : [];

  const filteredPaths = currentDiagram.paths.filter((path) => {
    // Quick filter: empty fields
    if (quickFilter === 'missing-label' && path.name?.trim()) return false;
    if (quickFilter === 'missing-description' && path.description?.trim()) return false;
    if (quickFilter === 'missing-auth' && path.authentication?.trim()) return false;

    // Text search
    if (!searchTerm) return true;
    const source = entitiesMap[path.source_id];
    const target = entitiesMap[path.target_id];
    const searchLower = searchTerm.toLowerCase();
    return (
      source?.name.toLowerCase().includes(searchLower) ||
      target?.name.toLowerCase().includes(searchLower) ||
      path.name?.toLowerCase().includes(searchLower) ||
      path.description?.toLowerCase().includes(searchLower) ||
      path.protocol?.toLowerCase().includes(searchLower) ||
      path.authentication?.toLowerCase().includes(searchLower)
    );
  });

  const copyToClipboard = () => {
    const text = currentDiagram.paths
      .map((path) => {
        const source = entitiesMap[path.source_id];
        const target = entitiesMap[path.target_id];
        const sourceBoundary = getEntityBoundaryChain(source?.boundary_id);
        const targetBoundary = getEntityBoundaryChain(target?.boundary_id);
        const boundaryColumn = `(${sourceBoundary || ''}) -> (${targetBoundary || ''})`;
        
        return `${source?.name || 'Unknown'} [${source?.type || ''}]\t${path.name || ''}\t${target?.name || 'Unknown'} [${target?.type || ''}]\t\t\t\t${path.protocol || ''}\t\t${path.authentication || ''}\t\t\t\t${boundaryColumn}`;
      })
      .join('\n');
      
    navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2500);
  };

  const openEditDialog = (path: Path) => {
    if (isEditDisabled) return;
    startEditing(path);
  };

  const startEditing = (path: Path) => {
    setEditingPath(path);
    setEditDraft({
      name: path.name ?? '',
      description: path.description ?? '',
      protocol: path.protocol ?? '',
      authentication: path.authentication ?? '',
    });
    setSaveError(null);
    setSaveSuccess(false);
  };

  const executeSavePath = async (updatedPath: Path, activeCookie?: string) => {
    if (isEditDisabled) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      if (isDraft) {
        // Save to draft payload in SPICE
        const updatedPaths = currentDiagram!.paths.map((p) =>
          p.id === updatedPath.id ? updatedPath : p,
        );
        const updatedDraft = await draftDiagramService.patchPayload(draftId, {
          paths: updatedPaths,
        });
        // Update local currentDiagram so UI reflects the change
        setAppState({
          currentDiagram: {
            ...currentDiagram!,
            paths: updatedPaths,
          },
        });
        onDraftUpdated?.(updatedDraft);
      } else {
        const useCookie = activeCookie ?? cookie;
        if (!useCookie) return;
        // Save to ThreatCat
        await threatcatService.updatePath({
          cookie: useCookie,
          id: updatedPath.id,
          uuid: updatedPath.uuid,
          name: updatedPath.name,
          description: updatedPath.description,
          authentication: updatedPath.authentication,
          protocol: updatedPath.protocol,
          source_id: updatedPath.source_id,
          target_id: updatedPath.target_id,
          diagram_id: updatedPath.diagram_id,
          position: updatedPath.position,
          style: updatedPath.style,
        });

        // Update local state so other tabs stay in sync
        const updatedDiagrams = diagrams.map((d, i) => {
          if (i !== currentDiagramIndex) return d;
          return {
            ...d,
            paths: d.paths.map((p) => (p.id === updatedPath.id ? updatedPath : p)),
          };
        });
        setAppState({ diagrams: updatedDiagrams });
      }

      setSaveSuccess(true);
      window.setTimeout(() => {
        setEditingPath(null);
        setSaveSuccess(false);
      }, 1200);
    } catch (err) {
      if (!isDraft && err instanceof ThreatCatAuthError) {
        disconnect();
        return;
      }
      setSaveError(err instanceof Error ? err.message : 'Failed to update path');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePath = async () => {
    if (!editingPath || isEditDisabled) return;

    const updatedPath: Path = {
      ...editingPath,
      name: editDraft.name,
      description: editDraft.description,
      protocol: editDraft.protocol,
      authentication: editDraft.authentication,
    };

    if (!isDraft && !cookie) {
      pendingSavePathRef.current = updatedPath;
      setConnectOpen(true);
      return;
    }

    await executeSavePath(updatedPath);
  };

  const handleConnected = () => {
    setConnectOpen(false);
    const nextCookie = getCookie();
    const pendingPath = pendingSavePathRef.current;
    pendingSavePathRef.current = null;
    if (isEditDisabled) return;
    if (pendingPath && nextCookie) {
      void executeSavePath(pendingPath, nextCookie);
    }
  };

  const handleConnectClose = () => {
    setConnectOpen(false);
    pendingSavePathRef.current = null;
  };

  return (
    <div className="space-y-4">
      <ConnectModal
        open={connectOpen}
        onClose={handleConnectClose}
        onConnected={handleConnected}
      />
      <Card>
        <CardHeader>
            <div className="flex items-center justify-between">
            <CardTitle>Detailed Connections Table</CardTitle>
            <div className="flex items-center space-x-3">
              {cookie && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  ThreatCat connected
                </span>
              )}
              <Button variant="outline" size="sm" onClick={copyToClipboard} disabled={copied}>
                <Copy className="mr-2 h-4 w-4" />
                {copied ? 'Copied' : 'Copy Data'}
              </Button>
              <span className="sr-only" aria-live="polite">{copied ? 'Connections copied to clipboard' : ''}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search connections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Filter className="h-3.5 w-3.5" />
              Quick filters:
            </div>
            <ToggleGroup
              type="single"
              value={quickFilter}
              onValueChange={(v) => setQuickFilter(v || 'all')}
              className="justify-start"
            >
              <ToggleGroupItem value="all" size="sm" className="text-xs px-3">
                All
              </ToggleGroupItem>
              <ToggleGroupItem value="missing-label" size="sm" className="text-xs px-3">
                Missing Label
                {missingLabelCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    {missingLabelCount}
                  </span>
                )}
              </ToggleGroupItem>
              <ToggleGroupItem value="missing-description" size="sm" className="text-xs px-3">
                Missing Description
                {missingDescCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    {missingDescCount}
                  </span>
                )}
              </ToggleGroupItem>
              <ToggleGroupItem value="missing-auth" size="sm" className="text-xs px-3">
                Missing Auth
                {missingAuthCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    {missingAuthCount}
                  </span>
                )}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="rounded-md border">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">#</th>
                    <th className="px-4 py-3 text-left font-medium">Source</th>
                    <th className="px-4 py-3 text-center font-medium"></th>
                    <th className="px-4 py-3 text-left font-medium">Target</th>
                    <th className="px-4 py-3 text-left font-medium">Label</th>
                    <th className="px-4 py-3 text-left font-medium">Description</th>
                    <th className="px-4 py-3 text-left font-medium">Protocol</th>
                    <th className="px-4 py-3 text-left font-medium">Authentication</th>
                    <th className="px-4 py-3 text-left font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredPaths.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                        No connections found
                      </td>
                    </tr>
                  ) : (
                    filteredPaths.map((path, index) => {
                      const source = entitiesMap[path.source_id];
                      const target = entitiesMap[path.target_id];
                      const sourceBoundary = getEntityBoundaryChain(source?.boundary_id);
                      const targetBoundary = getEntityBoundaryChain(target?.boundary_id);
                      const pathComments = reviewIndex.getPathComments(path.id, currentDiagram.id);

                      return (
                        <tr key={path.id} className="hover:bg-muted/50">
                          <td className="px-4 py-3 text-muted-foreground">{index + 1}</td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              <div className="font-medium">{source?.name || 'Unknown'}</div>
                              {sourceBoundary && (
                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                  {sourceBoundary}
                                </span>
                              )}
                              {source?.type === 'external' && (
                                <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                                  External
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-muted-foreground">→</td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              <div className="font-medium">{target?.name || 'Unknown'}</div>
                              {targetBoundary && (
                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                  {targetBoundary}
                                </span>
                              )}
                              {target?.type === 'external' && (
                                <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                                  External
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {path.name || <span className="text-muted-foreground italic">No label</span>}
                          </td>
                          <td className="px-4 py-3 max-w-[20rem]">
                            {path.description ? (
                              <HoverCard>
                                <HoverCardTrigger asChild>
                                  <div
                                    tabIndex={0}
                                    aria-label={`Description: ${path.description}`}
                                    className="truncate max-w-[20rem] whitespace-nowrap overflow-hidden"
                                  >
                                    {path.description.length > 20
                                      ? `${path.description.slice(0, 20)}…`
                                      : path.description}
                                  </div>
                                </HoverCardTrigger>
                                <HoverCardContent>
                                  <div className="text-sm">{path.description}</div>
                                </HoverCardContent>
                              </HoverCard>
                            ) : (
                              <span className="text-muted-foreground italic">No description</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-400/30">
                              {path.protocol || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {path.authentication || (
                              <span className="text-muted-foreground italic">Not documented</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <ReviewCommentsIndicator
                                count={pathComments.length}
                                onClick={() => setReviewModalPathId(path.id)}
                                triggerProps={{ 'data-path-review-trigger': path.id }}
                              />
                              <span title={editDisabledReason ?? undefined} className="inline-flex">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  aria-label="Edit connection"
                                  title={editDisabledReason ?? 'Edit and sync to ThreatCat'}
                                  disabled={isEditDisabled}
                                  onClick={() => openEditDialog(path)}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-sm text-muted-foreground">
            Showing {filteredPaths.length} of {currentDiagram.paths.length} connections
            {quickFilter !== 'all' && (
              <span className="ml-1">
                — filtered by <span className="font-medium">{quickFilter.replace('missing-', 'missing ')}</span>
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Review Comments Modal ────────────────────────────────────────────── */}
      <ReviewCommentsModal
        open={activeReviewPath !== null && activeReviewPathComments.length > 0}
        onClose={() => setReviewModalPathId(null)}
        targetType="Path"
        targetLabel={activeReviewPath?.name || `Connection #${activeReviewPath?.id ?? ''}`}
        targetSubtitle={
          activeReviewPath
            ? `${entitiesMap[activeReviewPath.source_id]?.name ?? 'Unknown'} → ${entitiesMap[activeReviewPath.target_id]?.name ?? 'Unknown'}`
            : undefined
        }
        comments={activeReviewPathComments}
      />

      {/* ── Edit Path Dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={editingPath !== null}
        onOpenChange={(open) => {
          if (!open) {
            pendingSavePathRef.current = null;
            setEditingPath(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Interface</DialogTitle>
            <DialogDescription>
              {editingPath && (() => {
                const source = entitiesMap[editingPath.source_id];
                const target = entitiesMap[editingPath.target_id];
                return `${source?.name ?? 'Unknown'} → ${target?.name ?? 'Unknown'} (ID: ${editingPath.id})`;
              })()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Label / Name</label>
              <Input
                value={editDraft.name}
                onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="Interface name…"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Description</label>
              <Textarea
                value={editDraft.description}
                onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))}
                placeholder="Description…"
                rows={3}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Protocol</label>
              <Input
                value={editDraft.protocol}
                onChange={(e) => setEditDraft((d) => ({ ...d, protocol: e.target.value }))}
                placeholder="e.g. HTTP/TLS"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Authentication</label>
              <Textarea
                value={editDraft.authentication}
                onChange={(e) => setEditDraft((d) => ({ ...d, authentication: e.target.value }))}
                placeholder="Authentication mechanism…"
                rows={2}
              />
            </div>

            {saveError && (
              <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {saveError}
              </div>
            )}
            {saveSuccess && (
              <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                Saved successfully
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                pendingSavePathRef.current = null;
                setEditingPath(null);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSavePath} disabled={isSaving}>
              {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : isDraft ? 'Save Draft' : 'Save & Sync'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
